/**
 * /try Submit Flow Helpers (result card + pipeline status + share resolve).
 *
 * WHAT THIS FILE DOES:
 * - Creates and labels the inline submit result card.
 * - Drives the pipeline cycle status copy while a Gist generates.
 * - Resolves the final share URL (streaming-instant vs ready-gated paths).
 * - Tags and emits the submit-to-navigate latency metric.
 *
 * WHY THIS EXISTS:
 * - Extracted verbatim from src/client/try-ui.ts so the controller stays
 *   under the god-module line. No behavior changes; identical copy strings.
 *
 * DESIGN NOTE (test contract):
 * - Helpers take the TryUIController instance and call back through its
 *   members (`host.showStatus(...)`, `host.client.*`, `host.readNowMs()`)
 *   so unit tests that stub instance members keep working unchanged.
 *
 * CALLED BY:
 * - src/client/try-ui.ts (handleSubmit/ingestContent and thin delegates)
 * - src/client/try-input-widgets.ts (upload progress status)
 *
 * @file src/client/try-submit-flow.ts
 */

import type { TryUIController } from './try-ui.js';
import type {
  IngestResponse,
  TryMediaIngestPhaseEvent
} from './try-shared.js';
import {
  TryMediaIngestPhases,
  WebTryError
} from './try-shared.js';
import { track as trackWebEvent, flush as flushWebEvents } from './web-telemetry.js';
import {
  resolveShareNavigationTarget,
  type ShareNavigationMode
} from './try-share-navigation.js';
import type { TryResultCardStatus } from './try-result-card.js';
import { createCard, updateCard } from './try-result-card.js';
import { extractSingleHttpUrl } from '../lib/url-input.js';
import { isDoclingDocumentFile } from '../lib/try-document-file.js';
import {
  GIST_PIPELINE_CYCLE_INITIAL_LABEL,
  GIST_PIPELINE_CYCLE_LABELS,
  GIST_PIPELINE_LABEL_CADENCE_MS,
  GistPipelineStatusLabels
} from '@gist-protocol/core/shared/gist-pipeline-status.js';

/**
 * Interval (ms) between pipeline cycle label advances on /try result cards.
 */
const PIPELINE_CYCLE_TICK_MS = GIST_PIPELINE_LABEL_CADENCE_MS;

export const TrySubmitNavigateMetric = Object.freeze({
  /**
   * Client-side submit-to-navigate latency metric (try-speed-plan measurement).
   */
  EVENT: 'try_submit_to_navigate_ms',
  MODES: {
    URL_SCRAPE: 'url_scrape',
    TEXT: 'text',
    PDF: 'pdf',
    MEDIA: 'media'
  }
} as const);

export type TrySubmitNavigateMode =
  typeof TrySubmitNavigateMetric.MODES[keyof typeof TrySubmitNavigateMetric.MODES];

/**
 * Creates the active result card while the submit event is still synchronous.
 */
export function createSubmitResultCard(host: TryUIController, content: string, file: File | null): string {
  const card = createCard({
    title: resolveSubmitCardTitle(host, content, file),
    sourceLabel: resolveSubmitCardSourceLabel(host, content, file),
    statusMessage: GistPipelineStatusLabels.LOADING
  });
  host.activeResultCardId = card.id;
  return card.id;
}

export function resolveSubmitCardTitle(host: TryUIController, content: string, file: File | null): string {
  if (file) {
    return file.name;
  }

  const url = extractUrl(host, content);
  if (url) {
    try {
      return new URL(url).hostname;
    } catch {
      return 'Web page';
    }
  }

  const firstLine = content.split('\n').find((line) => line.trim().length > 0)?.trim() ?? '';
  if (!firstLine) {
    return 'Text note';
  }
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}...` : firstLine;
}

export function resolveSubmitCardSourceLabel(host: TryUIController, content: string, file: File | null): string {
  if (file) {
    if (isDoclingDocumentFile(file)) {
      return 'Document';
    }
    return 'Media';
  }

  if (isUrl(host, content)) {
    return 'URL';
  }
  return 'Text';
}

export function showPipelineStatus(host: TryUIController, status: TryResultCardStatus, message: string): void {
  clearPipelineCycleCycler(host);
  showSubmitLifecycleStatus(host, status, message);
}

export function startPipelineCycleCycler(host: TryUIController): void {
  clearPipelineCycleCycler(host);
  showSubmitLifecycleStatus(host, 'processing', GIST_PIPELINE_CYCLE_INITIAL_LABEL);

  if (typeof window === 'undefined') {
    return;
  }
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    return;
  }

  let phaseIndex = 0;
  host.pipelineCycleTimerId = setInterval(() => {
    phaseIndex = (phaseIndex + 1) % GIST_PIPELINE_CYCLE_LABELS.length;
    showSubmitLifecycleStatus(host, 'processing', GIST_PIPELINE_CYCLE_LABELS[phaseIndex]);
  }, PIPELINE_CYCLE_TICK_MS);
}

export function clearPipelineCycleCycler(host: TryUIController): void {
  if (host.pipelineCycleTimerId !== null) {
    clearInterval(host.pipelineCycleTimerId);
    host.pipelineCycleTimerId = null;
  }
}

export function showSubmitLifecycleStatus(host: TryUIController, status: TryResultCardStatus, message: string): void {
  if (host.activeResultCardId) {
    updateCard(host.activeResultCardId, {
      status,
      statusMessage: message
    });
    return;
  }

  host.showStatus(message, 'loading');
}

/**
 * Resolves the share URL for submit finalization.
 *
 * Non-streaming cards can only say Ready after the content readiness guard
 * passes. The streaming branch intentionally opens an instant share early so
 * the share page can render its streaming/fallback shell.
 */
export async function resolveSubmitShareUrl(
  host: TryUIController,
  response: IngestResponse,
  navigationMode: ShareNavigationMode
): Promise<{
  shareUrl: string;
  usedInlineShare: boolean;
}> {
  const contentId = typeof response.contentId === 'string'
    ? response.contentId.trim()
    : '';

  if (!contentId) {
    if (response.publicUrl) {
      return {
        shareUrl: response.publicUrl,
        usedInlineShare: true
      };
    }
    throw new WebTryError('content_id_missing');
  }

  startPipelineCycleCycler(host);

  try {
    const canOpenStreamingShare = Boolean(response.wsUrl && response.wsToken);
    if (canOpenStreamingShare) {
      const initialShare = await host.client.createShareInstant({
        contentId,
        gistLanguage: host.selectedLanguage
      });
      const shareUrl = await resolveShareNavigationTarget({
        response,
        sharePromise: Promise.resolve({
          ...initialShare,
          publicUrl: resolveShareUrlWithInlineFallback(initialShare, response.publicUrl)
        }),
        navigationMode,
        onStatus: () => {
          /* Pipeline cycle owns user-facing status copy. */
        }
      });

      return {
        shareUrl,
        usedInlineShare: Boolean(response.publicUrl)
      };
    }

    const share = await host.client.createShareWhenReady({
      contentId,
      gistLanguage: host.selectedLanguage
    });

    const shareUrl = await resolveShareNavigationTarget({
      response,
      sharePromise: Promise.resolve({
        ...share,
        publicUrl: resolveShareUrlWithInlineFallback(share, response.publicUrl)
      }),
      navigationMode,
      onStatus: () => {
        /* Pipeline cycle owns user-facing status copy. */
      }
    });

    return {
      shareUrl,
      usedInlineShare: Boolean(response.publicUrl)
    };
  } finally {
    clearPipelineCycleCycler(host);
  }
}

export function resolveShareUrlWithInlineFallback(share: { publicUrl?: string }, inlinePublicUrl?: string): string | undefined {
  const shareUrl = typeof share.publicUrl === 'string' ? share.publicUrl.trim() : '';
  if (shareUrl.length > 0) {
    return shareUrl;
  }
  const fallback = typeof inlinePublicUrl === 'string' ? inlinePublicUrl.trim() : '';
  return fallback.length > 0 ? fallback : undefined;
}

/**
 * Best-effort share finalization for speculative URL hits.
 *
 * Speculation runs with `createShare` disabled to avoid creating shares before
 * an explicit submit action. On submit, if the speculative response already has
 * a contentId but no publicUrl, create the share directly from that contentId.
 *
 * Why:
 * - Avoids replaying `/api/web/ingest/url`, which can trigger duplicate
 *   scrape work when the first speculative ingest already succeeded.
 */
export async function ensureShareForSpeculativeUrlResponse(
  host: TryUIController,
  response: IngestResponse
): Promise<IngestResponse> {
  if (response.publicUrl || !response.contentId) {
    return response;
  }

  try {
    const share = await host.client.createShareInstant({
      contentId: response.contentId,
      gistLanguage: host.selectedLanguage
    });
    const publicUrl = typeof share.publicUrl === 'string' ? share.publicUrl.trim() : '';
    if (publicUrl) {
      return {
        ...response,
        publicUrl
      };
    }
  } catch {
    // Best-effort optimization only; fallback flow handles share creation retries.
  }

  return response;
}

/**
 * Emits submit-to-navigate latency as best-effort telemetry.
 *
 * Uses `keepalive` so the request can outlive page lifecycle transitions.
 */
export function emitSubmitToNavigateMetric(host: TryUIController, params: {
  submitStartedAtMs: number;
  mode: TrySubmitNavigateMode | null;
  usedInlineShare: boolean;
}): void {
  if (!params.mode) {
    return;
  }

  const elapsedMs = Math.max(0, Math.round(host.readNowMs() - params.submitStartedAtMs));
  trackWebEvent(TrySubmitNavigateMetric.EVENT, {
    durationMs: elapsedMs,
    mode: params.mode,
    usedInlineShare: params.usedInlineShare,
    authState: host.client.getAuthStateLabel()
  });
  flushWebEvents();
}

/**
 * Classifies the submit ingest mode for try_submit_to_navigate_ms tagging.
 */
export function resolveSubmitNavigateMode(host: TryUIController, params: {
  content: string;
  file: File | null;
}): TrySubmitNavigateMode | null {
  if (params.file) {
    const isPdf = params.file.type === 'application/pdf' || params.file.name.toLowerCase().endsWith('.pdf');
    return isPdf ? TrySubmitNavigateMetric.MODES.PDF : TrySubmitNavigateMetric.MODES.MEDIA;
  }

  if (isUrl(host, params.content)) {
    return TrySubmitNavigateMetric.MODES.URL_SCRAPE;
  }

  return TrySubmitNavigateMetric.MODES.TEXT;
}

/**
 * Applies media ingest phase transitions to unified pipeline status copy.
 */
export function handleMediaIngestPhaseChange(host: TryUIController, event: TryMediaIngestPhaseEvent): void {
  switch (event.phase) {
    case TryMediaIngestPhases.EXTRACTING_AUDIO:
    case TryMediaIngestPhases.COMPRESSING_AUDIO:
    case TryMediaIngestPhases.FALLING_BACK:
    case TryMediaIngestPhases.UPLOADING:
      showPipelineStatus(host, 'processing', GistPipelineStatusLabels.LOADING);
      return;
    default:
      return;
  }
}

/**
 * Checks if a string looks like a URL.
 */
export function isUrl(host: TryUIController, value: string): boolean {
  return extractUrl(host, value) !== null;
}

/**
 * Extracts the submit-ready URL, normalizing clean URLs like `example.com`.
 */
export function extractUrl(host: TryUIController, value: string): string | null {
  if (host.speculationController?.isUrl(value)) {
    return extractSingleHttpUrl(value);
  }
  return extractSingleHttpUrl(value);
}
